/**
 * sl-kypm.5.2: Pre-Publish Review — Sanitization Findings + Legal Disclaimer
 *
 * Displays sanitization results before publishing. Three severity levels:
 * - BLOCKED: Must fix before publish (publish button disabled)
 * - REVIEW: Requires per-finding confirmation checkbox
 * - INFO: Informational only, no action needed
 *
 * Also includes a legal disclaimer checkbox.
 * All must be satisfied before the wizard can proceed to the Consent step.
 *
 * SOLID:
 * - S: Only review UI — no sanitization logic, no publishing
 */
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useState } from 'react';

import { workspaceApiFetch } from '../../../modules/cloud';
import {
  ExtractedVarsEditor,
  type ParameterizedValue,
} from './extracted-vars-editor';
import * as styles from './styles.css';

// =============================================================================
// Types
// =============================================================================

export type SanitizationSeverity = 'blocked' | 'review' | 'info';

export interface SanitizationFinding {
  severity: SanitizationSeverity;
  rule: string;
  stepId?: string;
  message: string;
  location?: string;
  detectedValue?: string;
}

export interface SanitizationReviewResult {
  pass: boolean;
  findings: SanitizationFinding[];
}

interface StepNameMap {
  [stepId: string]: string;
}

// =============================================================================
// Severity Helpers
// =============================================================================

const SEVERITY_CONFIG: Record<
  SanitizationSeverity,
  { icon: string; label: string; color: string; bgMix: string }
> = {
  blocked: {
    icon: '\u26D4',
    label: 'BLOCKED',
    color: 'var(--streamlain-error-color, #D32F2F)',
    bgMix: '12%',
  },
  review: {
    icon: '\u26A0',
    label: 'REVIEW',
    color: '#d97706',
    bgMix: '10%',
  },
  info: {
    icon: '\u2139',
    label: 'INFO',
    color: 'var(--streamlain-info-color, #1976D2)',
    bgMix: '8%',
  },
};

// =============================================================================
// Finding Row
// =============================================================================

function FindingRow({
  finding,
  stepName,
  confirmed,
  onConfirm,
}: {
  finding: SanitizationFinding;
  stepName?: string;
  confirmed?: boolean;
  onConfirm?: (confirmed: boolean) => void;
}) {
  const t = useI18n();
  const config = SEVERITY_CONFIG[finding.severity];
  const severityLabel =
    finding.severity === 'blocked'
      ? t['com.streamlain.marketplace.publish.review.severity.blocked']()
      : finding.severity === 'review'
        ? t['com.streamlain.marketplace.publish.review.severity.review']()
        : t['com.streamlain.marketplace.publish.review.severity.info']();

  return (
    <div
      className={styles.credentialItem}
      style={{
        borderLeftWidth: '3px',
        borderLeftColor: config.color,
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Severity badge */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            color: config.color,
            background: `color-mix(in srgb, ${config.color} ${config.bgMix}, transparent)`,
          }}
        >
          {config.icon} {severityLabel}
        </span>

        {/* Step name */}
        {stepName && (
          <span
            className={styles.cardCategoryBadge}
            style={{ fontSize: '11px' }}
          >
            {stepName}
          </span>
        )}

        {/* Rule ID */}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            opacity: 0.5,
            fontFamily: 'monospace',
          }}
        >
          {finding.rule}
        </span>
      </div>

      {/* Message */}
      <div style={{ fontSize: '13px' }}>{finding.message}</div>

      {/* Detected value (masked) */}
      {finding.detectedValue && (
        <div
          style={{
            fontSize: '11px',
            fontFamily: 'monospace',
            opacity: 0.6,
            wordBreak: 'break-all',
          }}
        >
          {t['com.streamlain.marketplace.publish.review.detected']({
            value: finding.detectedValue,
          })}
        </div>
      )}

      {/* Location */}
      {finding.location && (
        <div style={{ fontSize: '11px', opacity: 0.5 }}>
          {t['com.streamlain.marketplace.publish.review.location']({
            location: finding.location,
          })}
        </div>
      )}

      {/* Review confirmation checkbox */}
      {finding.severity === 'review' && onConfirm && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '4px',
            fontSize: '12px',
            cursor: 'pointer',
            color: confirmed
              ? 'var(--streamlain-success-color, #2E7D32)'
              : undefined,
          }}
        >
          <input
            type="checkbox"
            checked={confirmed ?? false}
            onChange={e => onConfirm(e.target.checked)}
          />
          {t['com.streamlain.marketplace.publish.review.confirm-finding']()}
        </label>
      )}
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export interface PrePublishReviewProps {
  workspaceId?: string;
  automationBlockId?: string;
  templateType: 'step' | 'workflow' | 'automation';
  /** Called when the review state changes (controls whether wizard can proceed). */
  onReviewStateChange: (canProceed: boolean) => void;
  /**
   * sl-publish-preconfig.L3: Emits the merged parameterized-value list
   * (sanitizer output + publisher overrides) on every edit. Wizard sends
   * the array as `parameterizedValueOverrides` in the publish body. The
   * initial call (empty overrides) also lands here, so the wizard does
   * not need a separate fetch for parameterizedValues.
   */
  onParameterizedValuesChange?: (values: ParameterizedValue[]) => void;
}

export function PrePublishReview({
  workspaceId,
  automationBlockId,
  templateType,
  onReviewStateChange,
  onParameterizedValuesChange,
}: PrePublishReviewProps) {
  const t = useI18n();
  const [findings, setFindings] = useState<SanitizationFinding[]>([]);
  const [stepNames, setStepNames] = useState<StepNameMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmedReviews, setConfirmedReviews] = useState<Set<number>>(
    new Set()
  );
  const [legalConfirmed, setLegalConfirmed] = useState(false);
  const [parameterizedValues, setParameterizedValues] = useState<
    ParameterizedValue[]
  >([]);
  const [overrides, setOverrides] = useState<
    Record<string, Partial<ParameterizedValue>>
  >({});

  // Fetch sanitization results
  useEffect(() => {
    if (!workspaceId || !automationBlockId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    workspaceApiFetch(
      `/api/workspace/${workspaceId}/marketplace/analyze/${automationBlockId}?templateType=${templateType}`,
      { credentials: 'include' }
    )
      .then(res => {
        if (!res.ok) throw new Error(`Analysis failed: ${res.status}`);
        return res.json();
      })
      .then(
        (data: {
          findings: SanitizationFinding[];
          stepNames?: StepNameMap;
          parameterizedValues?: ParameterizedValue[];
        }) => {
          if (!cancelled) {
            setFindings(data.findings);
            setStepNames(data.stepNames ?? {});
            setParameterizedValues(data.parameterizedValues ?? []);
          }
        }
      )
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, automationBlockId, templateType]);

  // Categorize findings
  const blockedFindings = findings.filter(f => f.severity === 'blocked');
  const reviewFindings = findings.filter(f => f.severity === 'review');
  const infoFindings = findings.filter(f => f.severity === 'info');

  // Compute whether user can proceed
  const allReviewsConfirmed =
    reviewFindings.length === 0 ||
    reviewFindings.every((_, i) => confirmedReviews.has(i));
  const canProceed =
    blockedFindings.length === 0 && allReviewsConfirmed && legalConfirmed;

  // Notify parent of review state
  useEffect(() => {
    onReviewStateChange(canProceed && !isLoading);
  }, [canProceed, isLoading, onReviewStateChange]);

  const handleReviewConfirm = useCallback((index: number, checked: boolean) => {
    setConfirmedReviews(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  }, []);

  const handleOverrideChange = useCallback(
    (configKey: string, patch: Partial<ParameterizedValue>) => {
      setOverrides(prev => ({
        ...prev,
        [configKey]: { ...prev[configKey], ...patch },
      }));
    },
    []
  );

  // sl-publish-preconfig.L3: Emit the merged parameterized-value list on
  // every edit so the wizard's publish payload stays in sync.
  useEffect(() => {
    if (!onParameterizedValuesChange) return;
    if (parameterizedValues.length === 0) {
      onParameterizedValuesChange([]);
      return;
    }
    const merged = parameterizedValues.map(v => {
      const o = overrides[v.configKey];
      if (!o) return v;
      return { ...v, ...o };
    });
    onParameterizedValuesChange(merged);
  }, [parameterizedValues, overrides, onParameterizedValuesChange]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div className={styles.stateContainer}>
          <div className={styles.spinner} />
          {t['com.streamlain.marketplace.publish.review.loading']()}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div className={styles.credentialWarning}>
          {'\u26A0'}{' '}
          {t['com.streamlain.marketplace.publish.review.failed']({
            message: error,
          })}
        </div>
        <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
          {t['com.streamlain.marketplace.publish.review.failed-hint']()}
        </div>
      </div>
    );
  }

  // No findings — all clear
  if (findings.length === 0) {
    return (
      <div
        style={{
          padding: '20px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div className={styles.installSuccess}>
          <span className={styles.successCheckmark}>{'\u2713'}</span>
          {t['com.streamlain.marketplace.publish.review.clean']()}
        </div>

        <ExtractedVarsEditor
          values={parameterizedValues}
          overrides={overrides}
          onChange={handleOverrideChange}
        />

        {/* Legal disclaimer (always required) */}
        <LegalDisclaimer
          checked={legalConfirmed}
          onChange={setLegalConfirmed}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '20px',
        overflow: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.publish.review.title']()}
      </div>

      {/* Summary counts */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {blockedFindings.length > 0 && (
          <SeverityCount severity="blocked" count={blockedFindings.length} />
        )}
        {reviewFindings.length > 0 && (
          <SeverityCount
            severity="review"
            count={reviewFindings.length}
            confirmedCount={confirmedReviews.size}
          />
        )}
        {infoFindings.length > 0 && (
          <SeverityCount severity="info" count={infoFindings.length} />
        )}
      </div>

      {/* Blocked findings — must fix */}
      {blockedFindings.length > 0 && (
        <div>
          <div className={styles.credentialWarning}>
            {'\u26D4'}{' '}
            {t['com.streamlain.marketplace.publish.review.blocked']({
              count: String(blockedFindings.length),
            })}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              marginTop: '8px',
            }}
          >
            {blockedFindings.map((f, i) => (
              <FindingRow
                key={`blocked-${i}`}
                finding={f}
                stepName={f.stepId ? stepNames[f.stepId] : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Review findings — need confirmation */}
      {reviewFindings.length > 0 && (
        <div>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '6px',
            }}
          >
            {t['com.streamlain.marketplace.publish.review.confirmations']({
              confirmed: String(confirmedReviews.size),
              total: String(reviewFindings.length),
            })}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {reviewFindings.map((f, i) => (
              <FindingRow
                key={`review-${i}`}
                finding={f}
                stepName={f.stepId ? stepNames[f.stepId] : undefined}
                confirmed={confirmedReviews.has(i)}
                onConfirm={checked => handleReviewConfirm(i, checked)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Info findings — display only */}
      {infoFindings.length > 0 && (
        <div>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '6px',
            }}
          >
            {t['com.streamlain.marketplace.publish.review.information']()}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {infoFindings.map((f, i) => (
              <FindingRow
                key={`info-${i}`}
                finding={f}
                stepName={f.stepId ? stepNames[f.stepId] : undefined}
              />
            ))}
          </div>
        </div>
      )}

      <ExtractedVarsEditor
        values={parameterizedValues}
        overrides={overrides}
        onChange={handleOverrideChange}
      />

      {/* Legal disclaimer (always required) */}
      <LegalDisclaimer checked={legalConfirmed} onChange={setLegalConfirmed} />
    </div>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

function SeverityCount({
  severity,
  count,
  confirmedCount,
}: {
  severity: SanitizationSeverity;
  count: number;
  confirmedCount?: number;
}) {
  const t = useI18n();
  const config = SEVERITY_CONFIG[severity];
  const label =
    severity === 'blocked'
      ? t['com.streamlain.marketplace.publish.review.severity.blocked']()
      : severity === 'review'
        ? t['com.streamlain.marketplace.publish.review.severity.review']()
        : t['com.streamlain.marketplace.publish.review.severity.info']();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 600,
        color: config.color,
        background: `color-mix(in srgb, ${config.color} ${config.bgMix}, transparent)`,
      }}
    >
      {config.icon} {count} {label}
      {confirmedCount !== undefined && (
        <span style={{ fontWeight: 400 }}>
          (
          {t['com.streamlain.marketplace.publish.review.confirmed']({
            confirmed: String(confirmedCount),
            total: String(count),
          })}
          )
        </span>
      )}
    </span>
  );
}

function LegalDisclaimer({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const t = useI18n();
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '13px',
        cursor: 'pointer',
        background: checked
          ? 'color-mix(in srgb, var(--streamlain-success-color, #2E7D32) 6%, transparent)'
          : 'color-mix(in srgb, var(--streamlain-warning-color, #d97706) 6%, transparent)',
        border: `1px solid ${
          checked
            ? 'var(--streamlain-success-color, #2E7D32)'
            : 'color-mix(in srgb, var(--streamlain-warning-color, #d97706) 30%, transparent)'
        }`,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ marginTop: '2px' }}
      />
      <span>{t['com.streamlain.marketplace.publish.review.legal']()}</span>
    </label>
  );
}
